import {isShallowEqual} from '../../../../../helpers/reselectHelpers';
import {AppAction} from '../../../../appActions';
import {ContactsManagerSearchTabsEnum} from '../parameters/contactsManagerParameters';
import {ContactsManagerTableActionsEnum} from '../table/contactsManagerTableActions';
import {contactsManagerTableReducer} from '../table/contactsManagerTableReducer';
import {
  ContactsManagerTableState,
  tableStateIsAccountsTableState,
  tableStateIsContactsTableState,
  tableStateIsSearchCardGroupsTableState,
} from '../table/contactsManagerTableState';
import {ContactsManagerSearchActionsEnum} from './contactsManagerSearchActions';
import {ContactsManagerSearchState} from './contactsManagerSearchState';

/*
 * Redux.
 */

export function contactsManagerSearchReducer(
  state: ContactsManagerSearchState | undefined,
  action: AppAction,
  defaultTab?: ContactsManagerSearchTabsEnum,
): ContactsManagerSearchState | undefined {
  if (action.type === ContactsManagerSearchActionsEnum.CLEAN_UP) {
    return undefined;
  }

  const newState = {
    ...contactsManagerSearchBaseReducer(state, action, defaultTab),
    contactsTableState: contactsManagerTableReducerWrapper(
      state?.contactsTableState,
      action,
      tableStateIsContactsTableState,
    ),
    accountsTableState: contactsManagerTableReducerWrapper(
      state?.accountsTableState,
      action,
      tableStateIsAccountsTableState,
    ),
    cardGroupsTableState: contactsManagerTableReducerWrapper(
      state?.cardGroupsTableState,
      action,
      tableStateIsSearchCardGroupsTableState,
    ),
  };

  if (isShallowEqual(newState, state)) {
    return state;
  }

  return newState;
}

function contactsManagerSearchBaseReducer(
  state: ContactsManagerSearchState | undefined,
  action: AppAction,
  defaultTab?: ContactsManagerSearchTabsEnum,
): ContactsManagerSearchState {
  switch (action.type) {
    case ContactsManagerSearchActionsEnum.TAB_SELECTED: {
      return {
        ...(state ?? {}),
        selectedTab: action.payload.tab,
      };
    }
    default:
      if (!state) {
        return {
          selectedTab: defaultTab ?? ContactsManagerSearchTabsEnum.CONTACTS,
        };
      }
      return state;
  }
}

function contactsManagerTableReducerWrapper<T extends ContactsManagerTableState>(
  state: T | undefined,
  action: AppAction,
  tablePredicate: (table: ContactsManagerTableState) => table is T,
): T | undefined {
  // If we see requests for creating lists table, force reload or clean up table, we can ignore it.
  if (
    action.type === ContactsManagerTableActionsEnum.SET_UP_LISTS_TABLE ||
    action.type === ContactsManagerTableActionsEnum.CLEANUP ||
    action.type === ContactsManagerTableActionsEnum.FORCE_REFRESH
  ) {
    return state;
  }

  // If we are force reloading, we remove the table completely.
  if (action.type === ContactsManagerSearchActionsEnum.TABLES_FORCE_RELOADED) {
    return undefined;
  }

  const updatedTable = contactsManagerTableReducer(state, action);
  if (!updatedTable || tablePredicate(updatedTable)) {
    return updatedTable;
  }

  return state;
}
